import { authPasswordReset } from '@/fsd/entities/auth/api/authPasswordReset';
import { authPasswordResetConfirm } from '@/fsd/entities/auth/api/authPasswordResetConfirm';
import { validateEmail } from '@/fsd/entities/auth/lib/helpers/validateEmail'; // Updated import
import { useDebouncedEmailValidate } from '@/fsd/entities/auth/model/hooks/useDebouncedEmailValidate'; // Updated import
import { IAuthCodeStatus } from '@/fsd/entities/auth/model/types/IAuthCodeStatus';
import { PasswordResetFormErrors } from '@/fsd/entities/auth/model/types/PasswordResetFormErrors';
import { api } from '@/fsd/shared/api';
import { routes } from '@/fsd/shared/config/routes/routesConfig';
import { useThrottle } from '@/fsd/shared/hooks/useThrottle';
import { NotificationService } from '@/fsd/shared/lib/notifications';
import { useMutation } from '@tanstack/react-query';
import React, { MouseEventHandler, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validatePasswordResetForm } from '../../lib/helpers/validatePasswordResetForm';
import { INITIAL_FORM_ERRORS } from '../../model/consts/initialFormErrors';

export const usePasswordReset = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] =
    useState<PasswordResetFormErrors>(INITIAL_FORM_ERRORS);
  const [codeStatus, setCodeStatus] = useState<IAuthCodeStatus>();
  const [challengeId, setChallengeId] = useState<string>('');

  function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);
    setFormErrors((prev) => ({ ...prev, email: '' }));
  }

  function handleNewPasswordChange(event: React.ChangeEvent<HTMLInputElement>) {
    setNewPassword(event.target.value);
    setFormErrors((prev) => ({ ...prev, newPassword: '' }));
  }

  function handleConfirmPasswordChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    setConfirmPassword(event.target.value);
    setFormErrors((prev) => ({ ...prev, confirmPassword: '' }));
  }

  useDebouncedEmailValidate({ email: email, setFormErrors: setFormErrors });

  const {
    mutate: callAuthPasswordResetConfirm,
    status: authPasswordResetConfirmStatus,
  } = useMutation({
    mutationFn: async (code: string) => {
      await authPasswordResetConfirm({
        challenge_id: challengeId,
        code,
        new_password: newPassword,
      });
      navigate(routes.login.path);
    },
    retry: false,
  });

  const _sentAuthPasswordReset = async () => {
    try {
      const result = await authPasswordReset({ email });
      setChallengeId(result.data.challenge_id);
      setCodeStatus('sent');
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMessage =
        error.message || 'Произошла ошибка при попытке восстановления пароля.';
      setFormErrors((prev) => ({ ...prev, general: errorMessage }));
      throw error;
    }
  };

  const handleSubmit = async () => {
    let allErrors = { ...INITIAL_FORM_ERRORS };
    let hasAnyErrors = false;

    const { hasErrors: formValidationErrors, ...fieldErrors } =
      validatePasswordResetForm({ email, newPassword, confirmPassword });

    if (formValidationErrors) {
      allErrors = { ...allErrors, ...fieldErrors };
      hasAnyErrors = true;
    }

    if (hasAnyErrors) {
      setFormErrors(allErrors);
      return;
    }

    setFormErrors(INITIAL_FORM_ERRORS);

    await sentAuthPasswordReset();
  };

  const {
    throttledFunction: sentAuthPasswordReset,
    isThrottled,
    remainingTime,
  } = useThrottle(_sentAuthPasswordReset, 60000);

  const handleCodeComplete = useCallback(
    (code: string) => {
      callAuthPasswordResetConfirm(code);
    },
    [callAuthPasswordResetConfirm]
  );

  return {
    email,
    formErrors,
    handleEmailChange,
    handleSubmit,
    isThrottled,
    remainingTime,
    codeStatus,
    handleCodeComplete,
    authPasswordResetConfirmStatus,
    newPassword,
    confirmPassword,
    handleNewPasswordChange,
    handleConfirmPasswordChange,
  };
};
